/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * QuestionStatementString.ts - functions to render a question statement to string
 */

import { unreachable } from '../../../utils/typescript';
import { Format, isAdditiveSequenceQuestion, isEqualGroupsCountersQuestion, isEqualGroupsQuestion, isEquationQuestion, isOddOrEvenQuestion, isWordEquationQuestion, Question } from '../types';

export function formatQuestion(question: Question): string {
  if (isEquationQuestion(question)) {
    // Equation question
    const { numA, sign, numB, format } = question;

    switch (format) {
      case Format.Equation1:
        return `${numA} ${sign} ${numB} = __`;
      case Format.Equation2:
        return `__ = ${numA} ${sign} ${numB}`;
      case Format.Equation3:
        return `${numB} ${sign} ${numA} = __`;
      case Format.Equation4:
        return `__ = ${numB} ${sign} ${numA}`;
      case Format.Equation5:
        return `${numA} ${sign} __ = ${numB}`;
      case Format.Equation6:
        return `__ ${sign} ${numA} = ${numB}`;
      case Format.Equation7:
        return `${numB} = ${numA} ${sign} __`;
      case Format.Equation8:
        return `${numB} = __ ${sign} ${numA}`;
    }

  } else if (isWordEquationQuestion(question)) {
    // Word equation question
    return `${question.action} ${question.numA} is __`;

  } else if (isAdditiveSequenceQuestion(question)) {
    // Sequence question
    const { answer, sequence, format } = question;
    const num = (diff: number) => (answer + diff * sequence).toString();

    switch (format) {
      case Format.AdditiveSequence1:
        return ['?', num(1), num(2), num(3), num(4)].join(', ');
      case Format.AdditiveSequence2:
        return [num(-1), '?', num(1), num(2), num(3)].join(', ');
      case Format.AdditiveSequence3:
        return [num(-2), num(-1), '?', num(1), num(2)].join(', ');
      case Format.AdditiveSequence4:
        return [num(-3), num(-2), num(-1), '?', num(1)].join(', ');
      case Format.AdditiveSequence5:
        return [num(-4), num(-3), num(-2), num(-1), '?'].join(', ');
    }

  } else if (isOddOrEvenQuestion(question)) {
    // Odd or even question
    return `Is ${question.numA} ${question.action}`;

  } else if (isEqualGroupsQuestion(question)) {
    // Equal groups (images) question
    return `Are the groups equal? (${question.numA} and ${question.numB})`;

  } else if (isEqualGroupsCountersQuestion(question)) {
    // Equal groups (counters) question
    return `Are the groups equal? (${question.numA} and ${question.numB})`;

  } else {
    unreachable(question);
  }
}